import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole, useIsLoggedIn, useIsPlayingAsGuest, useIsAuthLoading } from '../../hooks';
import { searchUsers, changeUserStatus, changeUserRole } from '../../api/admin';
import './style.css';

const AdminPage = () => {
    const userRole      = useUserRole();
    const isLoggedIn    = useIsLoggedIn();
    const isGuest       = useIsPlayingAsGuest();
    const isAuthLoading = useIsAuthLoading();
    const navigate      = useNavigate();

    const [query, setQuery]         = useState('');
    const [users, setUsers]         = useState([]);
    const [page, setPage]           = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading]     = useState(false);
    const [msg, setMsg]             = useState('');
    const [error, setError]         = useState('');

    // Guard: wait for auth to resolve, then redirect non-admin/mod users
    useEffect(() => {
        if (isAuthLoading) return;
        if (!isLoggedIn || isGuest) { navigate('/'); return; }
        if (userRole !== 'admin' && userRole !== 'mod') { navigate('/'); }
    }, [isAuthLoading, isLoggedIn, isGuest, userRole, navigate]);

    const fetchUsers = useCallback(async (q = query, p = page) => {
        setLoading(true);
        try {
            const data = await searchUsers(q, p, 20);
            setUsers(data.users || []);
            setTotalPages(data.totalPages || 1);
            setError('');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load users.');
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { fetchUsers(query, page); }, [page]); // eslint-disable-line

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchUsers(query, 1);
    };

    const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

    const notifyError = (m) => { setError(m); setTimeout(() => setError(''), 3000); };

    const handleStatusChange = async (userId, status) => {
        try {
            await changeUserStatus(userId, status);
            notify(`Status updated to ${status}.`);
            fetchUsers(query, page);
        } catch (err) {
            notifyError(err.response?.data?.error || 'Failed to update status.');
        }
    };

    const handleRoleChange = async (userId, role) => {
        try {
            await changeUserRole(userId, role);
            notify(`Role updated to ${role}.`);
            fetchUsers(query, page);
        } catch (err) {
            notifyError(err.response?.data?.error || 'Failed to update role.');
        }
    };

    if (isAuthLoading || (userRole !== 'admin' && userRole !== 'mod')) return null;

    return (
        <div className="admin-page">
            <h1 className="admin-title">Admin Panel</h1>

            <form className="admin-search-form" onSubmit={handleSearch}>
                <input
                    className="admin-search-input"
                    type="text"
                    placeholder="Search by username or email..."
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
                <button className="admin-btn" type="submit">Search</button>
            </form>

            {msg && <div className="admin-msg admin-msg--success">{msg}</div>}
            {error && <div className="admin-msg admin-msg--error">{error}</div>}

            {loading ? (
                <div className="admin-loading">Loading...</div>
            ) : (
                <>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>ELO</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user._id} className={`admin-row admin-row--${user.status?.toLowerCase() || 'active'}`}>
                                    <td className="admin-username">{user.username}</td>
                                    <td className="admin-email">{user.email}</td>
                                    <td>
                                        <span className={`admin-badge admin-badge--role-${user.role}`}>{user.role}</span>
                                    </td>
                                    <td>
                                        <span className={`admin-badge admin-badge--${user.status?.toLowerCase() || 'active'}`}>
                                            {user.status || 'ACTIVE'}
                                        </span>
                                    </td>
                                    <td>{user.elo}</td>
                                    <td className="admin-actions">
                                        {user.role !== 'admin' && (
                                            <>
                                                {user.status !== 'BANNED' && (
                                                    <button
                                                        className="admin-btn admin-btn--danger admin-btn--sm"
                                                        onClick={() => handleStatusChange(user._id, 'BANNED')}
                                                    >
                                                        Ban
                                                    </button>
                                                )}
                                                {user.status === 'BANNED' && (
                                                    <button
                                                        className="admin-btn admin-btn--sm"
                                                        onClick={() => handleStatusChange(user._id, 'ACTIVE')}
                                                    >
                                                        Unban
                                                    </button>
                                                )}
                                                {userRole === 'admin' && user.role !== 'mod' && (
                                                    <button
                                                        className="admin-btn admin-btn--sm"
                                                        onClick={() => handleRoleChange(user._id, 'mod')}
                                                    >
                                                        Make Mod
                                                    </button>
                                                )}
                                                {userRole === 'admin' && user.role === 'mod' && (
                                                    <button
                                                        className="admin-btn admin-btn--sm admin-btn--danger"
                                                        onClick={() => handleRoleChange(user._id, 'user')}
                                                    >
                                                        Remove Mod
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        {user.role === 'admin' && (
                                            <span className="admin-protected">Protected</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="admin-pagination">
                            <button className="admin-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                            <span className="admin-page-label">Page {page} of {totalPages}</span>
                            <button className="admin-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminPage;
